// Utils PURI per l'editor colli dei popup di conferma invio (Cosma + WF).
// Tipo di dominio neutro {kg,l,a,p}. Le regole di aggregazione replicano il
// backend: il popup deve mostrare gli stessi numeri che produrrebbe il server.

/** Collo editabile: peso (kg) + dimensioni cartone (L/A/P cm). */
export type EditablePackage = { kg: number; l: number; a: number; p: number };

/** Arrotonda a 2 decimali (pesi in kg: evita 0.30000000000000004). */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function totalWeight(packages: EditablePackage[]): number {
  return round2(packages.reduce((sum, pkg) => sum + (pkg.kg || 0), 0));
}

/** Aggrega N colli in 1: peso = somma, dims = collo col volume maggiore
 *  (stessa regola del backend `_single_collo_override`). */
export function aggregatePackages(packages: EditablePackage[]): EditablePackage {
  if (packages.length === 0) return { kg: 0, l: 0, a: 0, p: 0 };
  const best = packages.reduce((acc, pkg) =>
    pkg.l * pkg.a * pkg.p > acc.l * acc.a * acc.p ? pkg : acc,
  );
  return { kg: totalWeight(packages), l: best.l, a: best.a, p: best.p };
}

/** Ricalcola i colli al cambio del numero colli: N=1 aggrega tutto (peso
 *  somma); N>=baseline = baseline + colli vuoti da compilare oltre; N
 *  intermedio = primi N-1 della baseline, l'ultimo aggrega il resto. Ritorna
 *  sempre copie nuove (immutabilità). */
export function recalcForCount(unitPackages: EditablePackage[], count: number): EditablePackage[] {
  const n = Math.max(1, Math.min(50, Math.floor(count) || 1));
  if (n >= unitPackages.length) {
    const extra = Array.from({ length: n - unitPackages.length }, () => ({
      kg: 0,
      l: 0,
      a: 0,
      p: 0,
    }));
    return [...unitPackages.map((pkg) => ({ ...pkg })), ...extra];
  }
  const head = unitPackages.slice(0, n - 1).map((pkg) => ({ ...pkg }));
  return [...head, aggregatePackages(unitPackages.slice(n - 1))];
}

/** True se due liste colli coincidono. Dirty tracking del popup: solo gli
 *  ordini modificati finiscono negli override (gli altri riusano la LDV in
 *  cache lato server). */
export function samePackages(a: EditablePackage[], b: EditablePackage[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((pkg, i) => {
    const other = b[i]!;
    return pkg.kg === other.kg && pkg.l === other.l && pkg.a === other.a && pkg.p === other.p;
  });
}

/** Colli non validi per l'invio (kg <= 0): gate del bottone Conferma — la
 *  LDV richiede sempre il peso. */
export function invalidPackagesCount(packages: EditablePackage[]): number {
  return packages.filter((pkg) => !(pkg.kg > 0)).length;
}
